/**
 * Mega menu injector.
 *
 * Theme blocks can't read each other's settings in Liquid, so each `mega-menu`
 * block renders its own featured panel into hidden <template>s keyed by the
 * parent menu item name. The header menu (desktop) and drawer (mobile) render
 * empty `.mega-menu-featured-slot` placeholders keyed by each menu item's title.
 * This script matches them up and clones the panel into the slot.
 *
 * The header's overflow-list component re-creates the menu items' DOM whenever
 * it recalculates which items fit (resize, font loading, hydration), which
 * wipes previously injected panels. A MutationObserver watches for that and
 * re-fills any slot that lost its content, so the panels never disappear.
 */
(function () {
  function normalize(value) {
    return (value || '').trim().toLowerCase();
  }

  function buildSourceMap(root) {
    var map = {};
    var sources = (root || document).querySelectorAll('.mega-menu-source');
    sources.forEach(function (source) {
      var key = normalize(source.getAttribute('data-mega-menu-parent'));
      if (!key) return;
      map[key] = {
        desktop: source.querySelector('template[data-mega-menu-variant="desktop"]'),
        mobile: source.querySelector('template[data-mega-menu-variant="mobile"]'),
        hideNative: source.getAttribute('data-mega-menu-hide-native') === 'true',
      };
    });
    return map;
  }

  /* Layout classes live on ancestors that can also be re-rendered, so re-apply
     them on every pass even when the slot itself is still filled. */
  function ensureLayoutClasses(slot, source, variant) {
    if (variant === 'desktop') {
      var inner = slot.closest('.menu-list__submenu-inner');
      if (inner) {
        inner.classList.add('menu-list__submenu-inner--has-featured');
        if (source.hideNative) {
          inner.classList.add('menu-list__submenu-inner--featured-only');
        }
      }
    } else if (variant === 'mobile' && source.hideNative) {
      var panel = slot.closest('.menu-drawer__inner-submenu');
      if (panel) {
        panel.classList.add('menu-drawer__inner-submenu--featured-only');
      } else {
        // levels < 3 accordion: slot lives inside <details>, not inner-submenu
        var details = slot.closest('details');
        if (details) details.classList.add('menu-drawer__details--featured-only');
      }
    }
  }

  function fillSlots(map, root) {
    var slots = (root || document).querySelectorAll('.mega-menu-featured-slot');
    slots.forEach(function (slot) {
      var key = normalize(slot.getAttribute('data-mega-menu-parent'));
      var variant = slot.getAttribute('data-mega-menu-variant') || 'desktop';
      var source = map[key];
      var template = source && source[variant];

      if (!template || !template.content || !template.content.childElementCount) {
        return;
      }

      // Re-fill when never filled, or when a re-render emptied the slot.
      if (slot.dataset.megaMenuFilled !== 'true' || slot.childElementCount === 0) {
        slot.replaceChildren(template.content.cloneNode(true));
        slot.dataset.megaMenuFilled = 'true';
      }

      slot.hidden = false;
      ensureLayoutClasses(slot, source, variant);
    });
  }

  function run() {
    var map = buildSourceMap(document);
    if (!Object.keys(map).length) return;
    fillSlots(map, document);
  }

  /* Coalesce bursts of mutations into one pass per frame. run() is idempotent
     and the observer only watches childList changes (not attributes), so the
     classes/hidden updates above can't re-trigger it in a loop. */
  var scheduled = false;
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(function () {
      scheduled = false;
      run();
    });
  }

  var observer = new MutationObserver(schedule);

  function observeContainers() {
    var containers = [];
    document.querySelectorAll('.mega-menu-featured-slot, .mega-menu-source').forEach(function (el) {
      var section = el.closest('.shopify-section') || el.closest('header') || document.body;
      if (containers.indexOf(section) === -1) containers.push(section);
    });
    if (!containers.length) containers.push(document.body);
    observer.disconnect();
    containers.forEach(function (container) {
      observer.observe(container, { childList: true, subtree: true });
    });
  }

  function init() {
    run();
    observeContainers();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-run after full load in case the menu's overflow list reflowed the DOM.
  window.addEventListener('load', schedule);

  // Re-run inside the theme editor when the header section is re-rendered
  // (the section's DOM is replaced, so re-attach the observer too).
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:block:select', schedule);

  /* ------------------------------------------------------------------ *
   * Click-to-open submenus.
   *
   * The hover/focus bindings were removed from the menu items in
   * snippets/header-menu.liquid; instead, clicking a top-level item with a
   * submenu toggles it via the theme's own header-menu component (so the
   * height/opacity animations keep working). Clicking elsewhere or pressing
   * Escape closes the open submenu.
   *
   * header-menu's `activate`/`deactivate` resolve the actual menu item link
   * via `findMenuItem(event.target)`, which expects `event.target` to be the
   * *container* (`.menu-list__list-item`) and looks up its `[ref="menuitem"]`
   * descendant — it does not match `event.target` itself. So every call below
   * passes the containing list item, never the link/button directly.
   * ------------------------------------------------------------------ */

  function closeOpenSubmenus() {
    document.querySelectorAll('header-menu').forEach(function (menu) {
      if (typeof menu.close === 'function') {
        menu.close();
        return;
      }
      if (typeof menu.deactivate !== 'function') return;
      var expandedLink = menu.querySelector('[ref="menuitem"][aria-expanded="true"]');
      if (!expandedLink) return;
      var expandedItem = expandedLink.closest('.menu-list__list-item') || expandedLink;
      menu.deactivate({ target: expandedItem });
    });
  }

  document.addEventListener('click', function (event) {
    var target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    var headerMenu = target.closest('header-menu');

    // Clicks outside the menu (including anywhere on the page) close it.
    if (!headerMenu) {
      closeOpenSubmenus();
      return;
    }

    var moreItem = target.closest('li[slot="more"]');
    if (moreItem) {
      event.preventDefault();
      if (headerMenu.dataset.overflowExpanded === 'true') {
        closeOpenSubmenus();
      } else if (typeof headerMenu.activate === 'function') {
        headerMenu.activate({ target: moreItem });
      }
      return;
    }

    var link = target.closest('.menu-list__link');
    if (link && link.getAttribute('aria-haspopup') === 'true') {
      // Top-level item with a submenu: toggle instead of navigating.
      event.preventDefault();
      var item = link.closest('.menu-list__list-item');
      if (!item) return;

      if (link.getAttribute('aria-expanded') === 'true') {
        if (typeof headerMenu.close === 'function') {
          headerMenu.close({ target: item });
        } else if (typeof headerMenu.deactivate === 'function') {
          headerMenu.deactivate({ target: item });
        }
      } else if (typeof headerMenu.activate === 'function') {
        headerMenu.activate({ target: item });
      }
      return;
    }

    // Click inside header-menu but not on a haspopup trigger (submenu links,
    // empty chrome, non-dropdown items): close any open submenu.
    if (!target.closest('.menu-list__submenu')) {
      closeOpenSubmenus();
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') closeOpenSubmenus();
  });
})();
